import React, { useContext, useEffect, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
  Box,
  Drawer,
  CssBaseline,
  Toolbar,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
  CircularProgress,
  Button,
} from '@mui/material';
import MuiAppBar from '@mui/material/AppBar';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert'; // Icon for menu
import { Outlet, useNavigate } from 'react-router-dom'; 
import { TitleContext } from '../controller/context/TitleContext';
import { DrawerContext } from '../controller/context/DrawerWidthContext';
import LanguageSelector from '../components/LanguageSelector';
import Loader from '../components/Loader';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import axios from 'axios';
import { API } from '../controller/api';
import Cookies from 'js-cookie';
import { UpdateContext } from '../controller/context/UpdateContext';
import { SelectedIDContext } from '../controller/context/SelectedIdContext';
import logo from '../assets/images/AI_logo.png'
import { formatDate } from '../controller/globalFunctions';
import { AccountCircle, Brightness4, Brightness7, Delete, Download, Edit, ModelTraining, OndemandVideo, Telegram, VideoCall, VideoFile } from '@mui/icons-material';
import { ThemeContext } from '../controller/context/ThemeContext';
import { useTranslateButton } from '../controller/context/TranslateButtonContext';

const drawerWidth = 300;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    padding: 5,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    
    paddingLeft: open ? '6px' : `-${drawerWidth}px`,
    marginLeft: open ? '0px' : `-${drawerWidth}px`,
    height: '100vh',
    overflow: 'hidden',
  })
);


const AppBar = styled(MuiAppBar, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    boxShadow:'none',

    backgroundImage: 'url("https://www.transparenttextures.com/patterns/always-grey.png")',
    backgroundSize: 'repeat',
    background:theme.palette.appbar.backgroundColor,
    color: '#346152',
    width: open ? `calc(100% - ${drawerWidth}px)` : '100%',
    marginLeft: open ? `${drawerWidth}px` : 0,
  })
);

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  background:theme.palette.appbar.backgroundColor,

  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

export default function Layout() {
  const { themeMode, toggleTheme } = useContext(ThemeContext);
  const { data, reloadTrigger, updateData } = useContext(UpdateContext);
  const { selectedId, updateSelectedId } = useContext(SelectedIDContext);
  const { title, setTitle } = useContext(TitleContext);
  const theme = useTheme();
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const { drawerWidth, setDrawerWidth } = useContext(DrawerContext);
  const [loading, setLoading] = useState(false);  // State to track loading status

  const [anchorEl, setAnchorEl] = useState(null); // For menu
  const [profileAnchorEl, setProfileAnchorEl] = useState(null); // For profile menu

  const [currentChatId, setCurrentChatId] = useState(null); // Track which chat's menu is open
  const [currentChat, setCurrentChat] = useState(null); // Track which chat's menu is open
  const { showTranslateButton, setShowTranslateButton } = useTranslateButton(); // Access the context

  const fetchUserData = async () => {
    const token = Cookies.get('token');
    try {
      const response = await axios.post(`${API}/auth/refresh-user`, { token });
      Cookies.set('userId', response.data.user.id, { expires: 7, secure: true, sameSite: 'Strict' });
      setUser(response.data.user);
      console.log(response.data.user)
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, [reloadTrigger]);

  const handleDrawerOpen = () => {
    setOpen(true);
    setDrawerWidth(300);
  };

  const handleDrawerClose = () => {
    setOpen(false);
    setDrawerWidth(0);
  };

  const handleMenuClick = (event, chatId, chat) => {
    setAnchorEl(event.currentTarget);
    setCurrentChatId(chatId);
    setCurrentChat(chat)
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentChatId(null);
  };

  const handleDeleteChat = async (chatId) => {
    try {
  
      // Send a DELETE request to the backend
      const response = await axios.post(`${API}/chat/delete`, {
        userId: Cookies.get('userId'),  // Assume userId is retrieved from cookies
        chatId: chatId,  // Send the chatId to the backend
      });
  
      // Log the response or update the UI if necessary
      if (response.status === 200) {
        console.log('Chat deleted successfully');
        // Perform any UI updates, such as removing the chat from the view
        updateData()
        navigate('/')
      } else {
        console.error('Failed to delete the chat:', response.data.message);
      }
    } catch (error) {
      console.error('Error deleting the chat:', error);
    } finally {
      handleMenuClose();  // Close the menu after the action is performed
    }
  };
  const downloadPdf = async (title, messages, entryMessage) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/document/generate-pdf`, { title, messages, entryMessage }, {
        responseType: 'arraybuffer'  // Important for binary data
      });
  
      // Create a Blob from the PDF data
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
  
      // Create a link to download the PDF
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${title}.pdf`);
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    } finally {
      setLoading(false);  // Set loading to false when the download is done (whether successful or not)
    }
  };
  
  
  

  const handleRenameChat = async (chatId) => {
    console.log(`Rename chat: ${chatId}`);
    const newTitle = window.prompt('Enter the new Title:');
  
    if (newTitle && newTitle.trim() !== '') {
      try {
        // Send a POST request to update the chat title
        const response = await axios.post(`${API}/chat/update-title`, {
          userId: Cookies.get('userId'),  // Send the userId from cookies
          chatId: chatId,  // Send the chatId to the backend
          newTitle: newTitle.trim(),  // Send the new title to the backend
        });
  
        if (response.status === 200) {
          console.log('Chat renamed successfully');
          // Optionally, update the UI with the new title
          updateData()
          // window.location.reload();
        } else {
          console.error('Failed to rename the chat:', response.data.message);
          window.alert('Error renaming chat.');
        }
      } catch (error) {
        console.error('Error renaming the chat:', error);
        window.alert('An error occurred while renaming the chat.');
      }
    } else {
      window.alert('Please input a valid title.');
    }
  
    handleMenuClose();
  };

  const handleProfileMenuOpen = (event) => setProfileAnchorEl(event.currentTarget);
  const handleProfileMenuClose = () => setProfileAnchorEl(null);


  const handleAccountSettings = () => {
    navigate('/account');
    handleProfileMenuClose();
  };

  const handleLogout = () => {
    // Perform logout actions (clear tokens, cookies, etc.)
    Cookies.remove('token');
    Cookies.remove('userId');
    navigate('/login');
    handleProfileMenuClose();
  };
  

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ mr: 2, borderRadius:5, display: open ? 'none' : 'block' }}
          >
            <MenuIcon htmlColor='#0077B6' />
          </IconButton>

          <Typography variant="h6" sx={{color: theme.palette.text.primary}} noWrap component="div">
            {title}
          </Typography>
      
          {/* Profile Icon and Menu */}
          <Box sx={{ position: 'absolute', right: 15, display: 'flex', alignItems: 'center' }}>
          <Button href="https://vimeo.com/1084643480?share=copy" target='_blank'>Demo Video</Button>
            <IconButton onClick={toggleTheme} color="inherit">
              {themeMode === 'dark' ? <Brightness7 htmlColor='#FFC708' /> : <Brightness4 htmlColor='#111936' />}
            </IconButton>
            {showTranslateButton && (
               <LanguageSelector />
            )}
      
            <IconButton onClick={handleProfileMenuOpen}>
              <AccountCircle htmlColor='#0077B6' />
            </IconButton>
            <Menu
              anchorEl={profileAnchorEl}
              open={Boolean(profileAnchorEl)}
              onClose={handleProfileMenuClose}
            >
              <MenuItem onClick={handleAccountSettings}>Account Settings</MenuItem>
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Box>
          
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            backgroundColor: theme.palette.appbar.backgroundColor,
            boxSizing: 'border-box',
            borderRight:'none',
            color: '#364152'
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'ltr' ? (
              <ChevronLeftIcon htmlColor="#0077B6" />
            ) : (
              <ChevronRightIcon htmlColor="#0077B6" />
            )}
          </IconButton>
        </DrawerHeader>
        <Box sx={{ textAlign: 'center' }}>
          <img 
            src={theme.palette.logo}
            alt="Logo"
            style={{ marginBottom: '1rem', width: 175 }}
          />
        </Box>
        <Divider/>
        {user?.subscription.training && (
          <>
          <ListItem disablePadding>
            <ListItemButton  
            // onClick={() => { setTitle(''); navigate('/training'); updateSelectedId(''); }}
            >
              <ListItemIcon sx={{marginLeft:3}}>
             <OndemandVideo htmlColor='dodgerBlue'/>
              </ListItemIcon>
              <a style={{textDecoration:'none', color:'inherit'}} href='https://app.aiforhrhub.com/#/deepdive?ref=1d86e8a9-849f-602a-4290-5cc1469da5a6' target='_blank'> <ListItemText  sx={{color: theme.palette.text.primary}} primary={<Typography variant="button" sx={{fontWeight:'bold', fontSize:14}}>AI Like a Boss™<br/> DEEP-DIVE TRAININGS</Typography>} /></a>
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton  
            // onClick={() => { setTitle(''); navigate('/training'); updateSelectedId(''); }}
            >
              <ListItemIcon sx={{marginLeft:3}}>
             <OndemandVideo htmlColor='dodgerBlue'/>
              </ListItemIcon>
              <a style={{textDecoration:'none', color:'inherit'}} href='https://app.aiforhrhub.com/#/sprint?ref=sdsteru-x654sd5f4' target='_blank'> <ListItemText  sx={{color: theme.palette.text.primary}} primary={<Typography variant="button" sx={{fontWeight:'bold', fontSize:14}}>AI Like a Boss™<br/> SPRINT SERIES 1</Typography>} /></a>
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton  
            // onClick={() => { setTitle(''); navigate('/training'); updateSelectedId(''); }}
            >
              <ListItemIcon sx={{marginLeft:3}}>
             <OndemandVideo htmlColor='dodgerBlue'/>
              </ListItemIcon>
              <a style={{textDecoration:'none', color:'inherit'}} href='https://app.aiforhrhub.com/#/sprint?ref=03c19e32-09c4-73ad-b0c7-97ba892a2404' target='_blank'> <ListItemText  sx={{color: theme.palette.text.primary}} primary={<Typography variant="button" sx={{fontWeight:'bold', fontSize:14}}>AI Like a Boss™<br/> SPRINT SERIES 2</Typography>} /></a>
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton  
            // onClick={() => { setTitle(''); navigate('/training'); updateSelectedId(''); }}
            >
              <ListItemIcon sx={{marginLeft:3}}>
             <OndemandVideo htmlColor='dodgerBlue'/>
              </ListItemIcon>
              <a style={{textDecoration:'none', color:'inherit'}} href='https://app.aiforhrhub.com/#/sprint?ref=564f50ab-cbd4-61b5-2efc-e1ec135a9f4e' target='_blank'> <ListItemText  sx={{color: theme.palette.text.primary}} primary={<Typography variant="button" sx={{fontWeight:'bold', fontSize:14}}>AI Like a Boss™<br/> SPRINT SERIES 3</Typography>} /></a>
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton  
            // onClick={() => { setTitle(''); navigate('/training'); updateSelectedId(''); }}
            >
              <ListItemIcon sx={{marginLeft:3}}>
             <OndemandVideo htmlColor='dodgerBlue'/>
              </ListItemIcon>
              <a style={{textDecoration:'none', color:'inherit'}} href='https://app.aiforhrhub.com/#/sprint?ref=55589e0d-03b9-a991-19d8-05794a771d26' target='_blank'> <ListItemText  sx={{color: theme.palette.text.primary}} primary={<Typography variant="button" sx={{fontWeight:'bold', fontSize:14}}>AI Like a Boss™<br/> SPRINT SERIES 4</Typography>} /></a>
            </ListItemButton>
          </ListItem>
        <Divider />
          </>
        )}

{user?.dealAiToken && (
          <>
           <ListItem disablePadding>
            <ListItemButton  
            // onClick={() => { setTitle(''); navigate('/training'); updateSelectedId(''); }}
            >
              <ListItemIcon sx={{marginLeft:3}}>
             <OndemandVideo htmlColor='dodgerBlue'/>
              </ListItemIcon>
              <ListItemText  sx={{color: theme.palette.text.primary}} primary={<><Typography variant="button" sx={{fontWeight:'bold', fontSize:16}}>AI for Business Hub App Dashboard</Typography></>} />
            </ListItemButton>
          </ListItem>
        <Divider />
          </>
        )}
       
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={() => { setTitle(''); navigate('/'); updateSelectedId(''); }}>
              <ListItemIcon sx={{marginLeft:3}}>
                <AddIcon htmlColor="#7FB800" />
              </ListItemIcon>
              <ListItemText sx={{color: theme.palette.text.primary}} primary={<Typography variant="button" sx={{fontWeight:'bold', fontSize:16}}>New Chat</Typography>} />
            </ListItemButton>
          </ListItem>
        </List>
        <Divider />
        <Typography sx={{ fontSize:16, textAlign: 'left !important', marginLeft: 4, marginTop:2,color: theme.palette.text.primary }} variant='button'><b>Recent Chats</b></Typography>
        <Divider />

        <Box sx={{ backgroundColor: theme.palette.background.default, padding: 1, borderRadius: 2, margin: 1, marginTop:0 }}>
          <List>
            {user?.chat.sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated)).map((i, index) => (
              <>
                <ListItem
                  sx={{ backgroundColor: i.id === selectedId ? '#0177b625' : '', borderRadius: 2,  }}
                  key={index}
                  disablePadding
                >
                  <ListItemButton onClick={() => { setTitle(i.appName); navigate('/chat', { state: { appName: i.appName, chatId: i.id, messages: i.messages, entryMessage: i.entryMessage, route: i.route } }); updateSelectedId(i.id); window.location.reload() }}>
                    <ListItemText
                    sx={{color: theme.palette.text.primary}}
                      primary={
                        <div>
                          <Typography sx={{ fontSize: 12, fontWeight:'bold' }}>{formatDate(i.dateCreated)}</Typography>
                          <Typography sx={{fontSize:15, fontWeight:'bold'}}>{i.title ? i.title : i.appName}</Typography>
                        </div>
                      }
                    />
                  </ListItemButton>
                  {/* Menu button for each chat */}
                  <IconButton onClick={(e) => handleMenuClick(e, i.id, i)}>
                    <MoreVertIcon />
                  </IconButton>
                </ListItem>
                <Divider sx={{ marginTop: 1, marginBottom: 1 }} />
              </>
            ))}
          </List>
        </Box>
      </Drawer>
      <Main open={open}>
        {/* <DrawerHeader /> */}
        <Outlet />
      </Main>
      <Loader />
      {/* Menu for chat options */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleRenameChat(currentChatId)}><Edit sx={{marginRight:2}} htmlColor='dodgerBlue'/> Rename</MenuItem>
        <MenuItem onClick={() => downloadPdf(currentChat.title, currentChat.messages, currentChat.entryMessage)}>
      {loading ? (
        <CircularProgress size={20} sx={{ marginRight: 2 }} />
      ) : (
        <Download sx={{ marginRight: 2 }} htmlColor='dodgerBlue' />
      )}
      Download
    </MenuItem>        <MenuItem onClick={() => handleDeleteChat(currentChatId)}><Delete sx={{marginRight:2}} htmlColor='tomato'/> Delete</MenuItem>
      </Menu>
    </Box>
  );
}
